import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'
import { authService } from './services/authService'
import { pointDebug } from './utils/pointDebug'
import { ROUTES } from '@shared/constants'
import ProtectedRoute from './components/ProtectedRoute'
import LoadingSpinner from './components/LoadingSpinner'

// Lazy load pages
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SignupPage = lazy(() => import('./pages/SignupPage'))
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'))
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then(module => ({ default: module.ForgotPasswordPage })))
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import SecurityPage from './pages/SecurityPage'
import AccountsPage from './pages/AccountsPage'
import VideoDetailPage from './pages/VideoDetailPage'

// New Admin Client (Lazy Loaded)
const AdminLoginPage = lazy(() => import('../admin-client/pages/AdminLoginPage').then(m => ({ default: m.AdminLoginPage })))
const AdminSetupPasswordPage = lazy(() => import('../admin-client/pages/SetupPasswordPage').then(m => ({ default: m.SetupPasswordPage })))
const AdminConsoleShell = lazy(() => import('../admin-client/pages/AdminConsoleShell'))


/**
 * Root application component with routing and authentication state.
 * Checks for existing session on mount to restore user state.
 */

function App() {

  const { setUser, loadAccounts, setAccounts } = useAuthStore()

  useEffect(() => {
    // 1. Initial load from localStorage (instant)
    loadAccounts()
    
    // 2. Refresh auth state and accounts in the background
    const initAuth = async () => {
      try {
        // Parallelize initial check for better performance
        const [user, accountsResp] = await Promise.all([
          authService.getCurrentUser().catch(() => null),
          authService.getAccounts().catch(() => ({ success: false, data: [] }))
        ])

        pointDebug('auth.bootstrap.result', {
          authenticated: Boolean(user),
          accountsRequestSuccess: accountsResp.success,
          accountCount: accountsResp.data?.length ?? 0,
        })
        
        setUser(user)
        if (accountsResp.success && accountsResp.data) {
          setAccounts(accountsResp.data)
        }
      } catch (error) {
        console.error("Auth initialization failed:", error)
        pointDebug('auth.bootstrap.error', {
          message: error instanceof Error ? error.message : String(error),
        })
        setUser(null)
      }
    }

    initAuth()
  }, [setUser, loadAccounts, setAccounts])

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <LoadingSpinner size="large" />
      </div>
    }>
      <Routes>
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />

        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.ACCOUNTS}
          element={
            <ProtectedRoute>
              <AccountsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.SECURITY}
          element={
            <ProtectedRoute>
              <SecurityPage />
            </ProtectedRoute>
          }
        />

        {/* NEW ADMIN SYSTEM (Obfuscated & Separate) */}
        <Route path="/console-99x/login" element={<AdminLoginPage />} />
        <Route path="/console-99x/setup-password" element={<AdminSetupPasswordPage />} />
        
        {/* NEW UNIFIED CONSOLE SHELL (Prevents White Flashes/Flickers) */}
        <Route path="/console-99x/*" element={<AdminConsoleShell />} />

        {/* Video detail page - MUST come before generic :username route */}
        <Route
          path="/dashboard/:username/video/:videoId"
          element={
            <ProtectedRoute>
              <VideoDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Dynamic route for specific account */}
        <Route
          path="/dashboard/:username"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute>
              <Navigate to={ROUTES.DASHBOARD} replace />
            </ProtectedRoute>
          }
        />

        {/* Catch-all: redirect unmatched routes to login */}
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    </Suspense>
  )
}

export default App
